import { useEffect, useMemo, useRef, useState } from "react";

import { AgGridReact } from "ag-grid-react";
import Box from "@mui/material/Box";
import { green, red } from "@mui/material/colors";

import { withTranslation } from "react-i18next";
import { debounce, isEmpty } from "lodash";
import { addDays, format } from "date-fns";
import { DefaultTooltipContent } from "recharts";

import fpMaterialPsiService from "@plannel/services/fp/fp-material-psi-service";
import numberUtil from "@plannel/utils/number-util";
import VersionSearchGroup from "@plannel/pages/factory-plan/common/component/VersionSearchGroup";
import PeriodSearchGroup, { BUCKET_CASE } from "@plannel/pages/factory-plan/common/component/PeriodSearchGroup";
import SearchOptsGroupRetrieve from "@plannel/pages/factory-plan/common/component/SearchOptsGroupRetrieve";
import FpGridSetting, {
  readHide, buildGroupColumnPair, REPORT_TREE_GRID_OPTIONS, buildReportStatusBar,
  applyColumnOrder, buildDayBucketPivotColumnDefs, consumePendingFocus
} from "@plannel/pages/factory-plan/common/grid";
import { buildXAxisSortMap } from "@plannel/pages/factory-plan/common/chart";
import { getFilterStore } from "@plannel/components/aggrid/FilterStatusPanel";
import GridUtils from "@plannel/components/aggrid/GridUtils";
import FilterContainer from "@plannel/components/layout/FilterContainer";
import SplitterContainer from "@plannel/components/SplitterContainer";
import PivotChartComposed from "@plannel/components/rechart/PivotChartComposed";
import Snackbar from "@plannel/components/Snackbar";
import { DownloadButton } from "@plannel/components/ActionIconButton";
import ColorUtil from "@plannel/pages/utils/color-util";
import reduxUtil from "@plannel/utils/redux-util";
import debug from "@plannel/utils/debug-util";

const { snackBarMessageColor } = ColorUtil;

// old FP_SRH_RANGE.attr01Val(DB 단일 고정값) 이식 — PlanResult/ResourceUtilization 파일럿에서
// 실제 확인된 값(9)을 그대로 재사용한다. 화면/뷰별로 분기되는 게 아니라 테넌트에 저장된 단일 값이라
// (fp-report-screen-guide.md §A-3c), old MaterialPsi.jsx 소스의 로컬 fallback(14, DB 로드 전
// 임시값 `let fpSettings = { searchZone: 14 }`)이 아니라 그 실제 DB 값을 이식한다.
// TODO(MIG-T2): [materialpsi/MaterialPsi] 조회조건 세팅 3종(버킷 목록/검색필드 표시/검색구간)의
//   테넌트별 커스터마이징 미이관.
//   old: search-option/bucket · search-option/srh-options · setting FP_SRH_RANGE를 각각 DB에서 조회
//   사유: 정적 구성(JSON 카탈로그/고정 카탈로그/상수)으로 전환 — 가이드 §A-3~§A-3d에서 결정
const SEARCH_ZONE_DAYS = 9;

// old SearchOptsGroup이 이 화면에서 실제로 쓰던 필드(loadData/loadChartData의 getValues 기준) —
// itemCdNm 하나뿐이다(PlanResult/ResourceUtilization과 달리 자원 검색 필드가 없다).
const SEARCH_FIELD_LABELS = { itemCdNm: "FP_ITEM" };
const SEARCH_FIELD_KEYS = Object.keys(SEARCH_FIELD_LABELS);

const PARAM_DATE_FORMAT = "yyyyMMdd";

// old MaterialPsiChart.jsx의 INDICATOR_CONFIG(themeStore.t3smartscm.fpChartPriority) 이식 —
// 백엔드(MaterialPsiService)가 항상 이 5개 지표만 내려주므로(PlanResult의 itemCd처럼 클릭 시점에
// 동적으로 시리즈를 조립할 필요 없이) ResourceUtilization과 동일하게 화면 고정 배열로 선언한다.
// direction: old `makeChartDataset`의 `direction`과 동일한 의미 — 백엔드는 항상 양수(절대값)로
// 내려주므로(MaterialPsiService 확인) 프론트에서 'minus'인 지표만 음수로 뒤집어 그린다(입고=위,
// 소비(만료+필요수량)=아래로 갈라지는 PSI 워터폴 형태). type:"line" 2종(기초/기말 재고)은 우측 축,
// type:"bar" 3종(입고/만료/필요수량)은 좌측 축 — old도 rightYAxis(재고 추이)/y(수량)로 분리했다.
const INDICATOR_ORDER = ["FP_INIT_STOCK", "FP_TO_BE_INPUT", "FP_EXPIRED_QTY", "FP_REQ_QTY", "FP_ENDING_STOCK"];
const INDICATOR_CONFIG = {
  FP_INIT_STOCK: { type: "line", orientation: "right", color: "#4472C4", dashed: false, direction: "plus" },
  FP_ENDING_STOCK: { type: "line", orientation: "right", color: "#4472C4", dashed: true, direction: "plus" },
  FP_TO_BE_INPUT: { type: "bar", orientation: "left", color: "#6DE36F", stackId: "input", direction: "plus" },
  FP_EXPIRED_QTY: { type: "bar", orientation: "left", color: "#FFC950", stackId: "consumption", direction: "minus" },
  FP_REQ_QTY: { type: "bar", orientation: "left", color: "#FC5F67", stackId: "consumption", direction: "minus" },
};

// old MaterialPsiChart.jsx는 getDefaultChartOptions(useAbsoluteValue=true)로 툴팁 값에 항상
// Math.abs()를 적용했다(direction:minus로 뒤집은 지표도 부호 없이 보이는 이유) — PivotChartComposed
// (공용)의 기본 툴팁 포맷터는 음수면 "-"를 무조건 다시 붙여 화면별로 끌 수 없다. customTooltip으로
// recharts 기본 툴팁(DefaultTooltipContent)을 재사용하면서 formatter만 교체한다
// (fp-report-screen-guide.md §A-7, ProdSalesStockStatus.js와 동일 패턴).
const AbsValueTooltip = (props) => (
  <DefaultTooltipContent {...props} formatter={(value) => numberUtil.formatNumber(Math.abs(value))} />
);

// 트리(행 그룹) 계층 — 공장 > 품목(leaf는 analysisIndicator, 5개 고정 지표). old 컬럼정의
// (`plantGroup`/`itemGroup` 모두 `mergeRule`)를 근거로 Row Grouping을 선택했다(Tree Data가 아님,
// fp-report-screen-guide.md §A-6b — old가 LocalTreeDataProvider가 아니라 mergeRule을 쓰고
// 백엔드 응답도 평평한 목록이라 판단 기준과 일치). PlanResult/ResourceUtilization과 달리 레벨이
// 2단뿐이다(자원/스테이지 레벨이 old 컬럼정의에 없음).
const GROUP_FIELDS = ["plantCd", "plantNm", "itemCd", "itemNm"];

const PLANT_CD_COL_ID = "plantCdGrp";
const PLANT_NM_COL_ID = `PLANT_GRP_NM${GridUtils.IGNORE_P13N_COLLID}`;
const ITEM_CD_COL_ID = "itemCdGrp";
const ITEM_NM_COL_ID = `ITEM_GRP_NM${GridUtils.IGNORE_P13N_COLLID}`;

const PLANT_NM_HIDE_KEY = "plantNmColsHide";
const ITEM_NM_HIDE_KEY = "itemNmColsHide";

const MaterialPsi = ({ t, viewName, title }) => {
  const reduxViewState = reduxUtil.getViewState(viewName);

  const [versionState, setVersionState] = useState({
    planScope: "", planningDate: null, versionCd: "", plantCd: [], selectedVersion: null
  });
  const [periodState, setPeriodState] = useState({
    startDate: null, endDate: null, bucketType: "", isEntirePlanPeriod: true
  });
  // itemCdNm — Report 공통 정책(§A-9b)대로 조회 버튼이 없다. 자유 텍스트는
  // SearchOptsGroupRetrieve(TextFieldSearch 기반) 안의 Enter/검색아이콘으로만 커밋한다.
  const itemCdNmRef = useRef("");

  const [snackInfo, setSnackInfo] = useState({ open: false, severity: "", content: "" });

  const gridRef = useRef();
  const [rowData, setRowData] = useState([]);

  const [chartData, setChartData] = useState([]);
  const [chartLabels, setChartLabels] = useState([]);

  // 응답 순서 뒤바뀜 방지 — 조회(그리드)와 차트는 서로 다른 요청이라 가드도 2개(§0-8).
  const latestGridReqRef = useRef(0);
  const latestChartReqRef = useRef(0);
  const prevChartParamsRef = useRef(null);
  const pendingFocusRef = useRef(null);
  const searchStateRef = useRef({ versionState, periodState });
  searchStateRef.current = { versionState, periodState };

  const debouncedRetrieveRef = useRef();
  if (!debouncedRetrieveRef.current) {
    debouncedRetrieveRef.current = debounce(() => retrieveMaterialPsi(), 200);
  }
  useEffect(() => () => debouncedRetrieveRef.current.cancel(), []);

  useEffect(() => () => getFilterStore(viewName).clear(), [viewName]);

  const defaultGridMemo = useMemo(() => {
    const base = FpGridSetting({ title, viewName });
    return {
      ...base,
      // 조회 전용 화면 — 공용 DefaultGridSetting의 defaultColDef.editable:true를 끈다.
      defaultColDef: { ...base.defaultColDef, editable: false },
      // 핵심 지표(필요수량) 강조는 행 전체가 아니라 분석 지표 컬럼~피벗 컬럼에만 cellStyle로
      // 건다(아래 analysisIndicatorHighlightStyle/pivotCellStyle) — 공용 DataState.getRowStyles
      // (편집 상태 표시용, read-only 화면엔 무의미)를 끈다.
      getRowStyle: () => null,
      // 행 그룹(트리) 3종 세트 — PlanResult/ResourceUtilization과 동일한 조합.
      ...REPORT_TREE_GRID_OPTIONS,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 핵심 지표(필요수량) 강조 — 분석 지표 컬럼에 건다. 배경색만(DP/MP/RP 전반 green[50] 관례,
  // 굵게는 안 씀) — 아래 pivotCellStyle과 동일 기준으로 분석 지표 컬럼~피벗 컬럼 범위에만 적용된다.
  const analysisIndicatorHighlightStyle = (params) => (
    params.data?.analysisIndicator === "FP_REQ_QTY" ? { backgroundColor: green[50] } : null
  );

  // 고정(왼쪽 pinned) 컬럼 — old fixColumns(plantCd/plantNm/itemCd/itemNm/initStockQty/
  // analysisIndicator)와 1:1. 공장/품목은 행 그룹(트리)으로, initStockQty는(자원 부하 분석의
  // toolResourceYn과 같은 계열 — mergeRule이 걸려 있었지만 값이 품목 단위로 항상 동일해 그룹으로
  // 만들 필요가 없다) 평범한 field 컬럼, analysisIndicator는 트리의 leaf(실제 데이터 행을 구분하는
  // 값)라 field 컬럼이다.
  const fixedColumnDefs = useMemo(() => [
    ...GROUP_FIELDS.map(field => ({
      headerName: field, field, colId: field, rowGroup: true, hide: true, suppressColumnsToolPanel: true
    })),

    ...buildGroupColumnPair({
      viewName, hidden: readHide(reduxViewState, PLANT_NM_HIDE_KEY),
      cdColId: PLANT_CD_COL_ID, cdField: "plantCd", cdHeader: "FP_PLANT_CD",
      nmColId: PLANT_NM_COL_ID, nmField: "plantNm", nmHeader: "PLANT_NM",
      hideKey: PLANT_NM_HIDE_KEY, width: 150
    }),
    ...buildGroupColumnPair({
      viewName, hidden: readHide(reduxViewState, ITEM_NM_HIDE_KEY),
      cdColId: ITEM_CD_COL_ID, cdField: "itemCd", cdHeader: "FP_ITEM_CD",
      nmColId: ITEM_NM_COL_ID, nmField: "itemNm", nmHeader: "ITEM_NM",
      hideKey: ITEM_NM_HIDE_KEY, width: 150
    }),

    // initStockQty — old dataType:"number", mergeRule로 품목 단위 반복 표시. 그룹이 아니라
    // 평범한 number 컬럼(읽기 전용이라 defaultColDef.editable:false로 클릭해도 값이 안 바뀐다).
    {
      headerName: "FP_PREV_BUCKET_INIT_QTY", field: "initStockQty", colId: "initStockQty", width: 130,
      pinned: "left", lockPinned: true, type: ["numberColumn", "rightAligned"]
    },

    // leaf(분석 지표) — old displayCallback가 항상 transLangKey(value)로 번역해서 보여준다
    // (PlanResult의 itemCd처럼 SUM일 때만이 아니라 매번 번역, ResourceUtilization의
    // analysisIndicator와 동일한 방식). cellStyle은 핵심 지표(필요수량) 강조 — 분석 지표
    // 컬럼부터 피벗 컬럼까지만 걸고(고정된 공장/품목/이전 기초 재고 컬럼엔 안 건다) 배경색만
    // 쓴다(DP/MP/RP 전반의 green[50] 관례와 동일, 굵게는 안 씀 — 아래 pivotCellStyle과 동일 기준).
    {
      headerName: "FP_ANALYSIS_INDICATOR", field: "analysisIndicator", colId: "analysisIndicator", width: 150,
      pinned: "left", lockPinned: true, type: ["nonEditableColumn"],
      valueFormatter: (params) => t(params.value),
      cellStyle: analysisIndicatorHighlightStyle
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [t]);

  // old setCrossTabGridLayoutAndOptions의 styleCallback(피벗 데이터 셀 전용, 고정 컬럼엔 안 걸림) —
  // analysisIndicator==='FP_REQ_QTY' 행은 green[50] 배경(굵게 없음, DP/MP/RP 관례),
  // analysisIndicator==='FP_ENDING_STOCK' 행에서 값<0인 피벗 셀은 'shortage-cell'(빨강+굵게)로 별개 유지.
  const pivotCellStyle = (params) => {
    const indicator = params.data?.analysisIndicator;
    if (indicator === "FP_REQ_QTY") return { backgroundColor: green[50] };
    if (indicator === "FP_ENDING_STOCK" && params.value < 0) return { color: red[500], fontWeight: "bold" };
    return null;
  };

  // 요일 코드 번역+토/일 강조색은 common/grid.js buildDayBucketPivotColumnDefs가 처리한다
  // (old setColumnColorOnSunSat 대응, §A-6d/§A-6f). pivotCellStyle은 이 화면 고유 강조라
  // childExtraProps로 얹는다.
  const buildPivotColumnDefs = (crosstabHeader) => buildDayBucketPivotColumnDefs(crosstabHeader, t, {
    childExtraProps: () => ({ cellStyle: pivotCellStyle })
  });

  // old setCrossTabGridDataWithBucket — pivotData[i]를 crosstabHeader[i] 필드로 펼친다.
  const buildRowData = (crosstabHeader, data) => (data ?? []).map(row => {
    const flat = {
      plantCd: row.plantCd, plantNm: row.plantNm,
      itemCd: row.itemCd, itemNm: row.itemNm,
      initStockQty: row.initStockQty, analysisIndicator: row.analysisIndicator
    };
    (crosstabHeader ?? []).forEach((header, index) => {
      flat[header] = row.pivotData?.[index];
    });
    return flat;
  });

  const toParamDate = (date) => (date ? format(date, PARAM_DATE_FORMAT) : null);

  const buildBaseParams = () => {
    const { versionState: version, periodState: period } = searchStateRef.current;
    return {
      viewId: viewName,
      subId: "",
      planScope: version.planScope,
      versionCd: version.versionCd,
      itemCdNm: itemCdNmRef.current,
      bucketType: period.bucketType,
      fromDt: toParamDate(period.startDate),
      toDt: toParamDate(period.endDate),
      usePlanPeriod: !!period.isEntirePlanPeriod
    };
  };

  const clearChart = () => {
    prevChartParamsRef.current = null;
    setChartData([]);
    setChartLabels([]);
    getFilterStore(viewName).clear();
  };

  // 하단 상태바 브레드크럼 — 공장/품목 2단뿐이라 originLoc/item 네이티브 슬롯을 그대로 쓴다
  // (PlanResult/ResourceUtilization처럼 destLoc 슬롯을 자원/스테이지용으로 빌려 쓸 필요가 없다).
  const updateStatusBreadcrumb = (target) => {
    getFilterStore(viewName).setValue({
      originLocCd: target.plantCd ?? null, originLocCdHeader: t("FP_PLANT_CD"),
      originLocNm: target.plantNm ?? null, originLocNmHeader: t("PLANT_NM"),
      itemCd: target.itemCd ?? null, itemCdHeader: t("FP_ITEM_CD"),
      itemNm: target.itemNm ?? null, itemNmHeader: t("ITEM_NM")
    });
  };

  // 클릭 기준으로 차트를 다시 조회한다. old updateChartFromGrid와 동일 — 상위(공장) 레벨을
  // 클릭하면 하위(품목) 코드는 비워서 보낸다(백엔드가 useGrouping:true일 때 이 레벨로 집계).
  const loadChart = (target) => {
    const { versionState: version } = searchStateRef.current;
    if (!version.planScope || !version.versionCd) return;

    updateStatusBreadcrumb(target);

    const nextParams = { plantCd: target.plantCd ?? "", itemCd: target.itemCd ?? "" };
    const prev = prevChartParamsRef.current;
    if (prev && prev.plantCd === nextParams.plantCd && prev.itemCd === nextParams.itemCd) {
      return;
    }
    prevChartParamsRef.current = nextParams;

    const reqId = ++latestChartReqRef.current;
    const params = { ...buildBaseParams(), ...nextParams, useGrouping: true };

    // TODO(MIG-T2): [materialpsi/MaterialPsi] 그리드 컬럼 필터(값 목록 체크박스) 미이관.
    //   old: plantCd/itemCd에 RealGrid autoFilter, 필터 선택값을 차트 조회의 changes(FilterParameter:
    //        plantCds/itemCds/analysisIndicators)로 함께 전송
    //   사유: 행 그룹(트리)으로 전환하면서 코드 컬럼이 hide:true인 데이터 컬럼이 돼 헤더 필터 아이콘을
    //        띄울 자리가 없어졌다. 백엔드는 changes를 계속 받으므로 2차에 트리거 UI만 얹으면 된다
    //        (가이드 §A-6e와 동일한 결정) — 지금은 빈 필터로 보낸다.
    fpMaterialPsiService.getMaterialPsiChart(params, {})
      .then(response => {
        if (reqId !== latestChartReqRef.current) return;

        const { chartHeader, data } = response.data ?? {};
        const rows = data ?? [];

        if (isEmpty(rows) || isEmpty(chartHeader)) {
          setChartData([]);
          setChartLabels([]);
          return;
        }

        setChartLabels(chartHeader);
        // old makeChartDataset — 지표별로 한 행. 백엔드가 항상 양수(절대값)로 내려주므로
        // (MaterialPsiService 확인) direction:'minus'인 지표(만료/필요수량)만 프론트에서 음수로
        // 뒤집어 워터폴 형태로 그린다(§ 상단 INDICATOR_CONFIG 주석 참고).
        // TODO(MIG-T2): [materialpsi/MaterialPsi] 값이 전부 0/없는 지표 시리즈 자동 숨김 미이관.
        //   old: makeChartDataset 호출 전 hasValue 체크로 그런 지표는 datasets에서 완전히 제외
        //   사유: 1차 목표(조회·표시)에 불필요 — 값이 0이면 막대/선이 평평하게(높이 0) 그려질 뿐 표시
        //        자체는 정확함. 범례에 불필요한 항목이 남는 정도의 장식성 차이.
        setChartData(rows.map(r => {
          const config = INDICATOR_CONFIG[r.analysisIndicator];
          const sign = config?.direction === "minus" ? -1 : 1;
          const point = { analysisIndicator: r.analysisIndicator };
          chartHeader.forEach((header, index) => {
            const raw = r.pivotData?.[index];
            point[header] = raw != null ? sign * Math.abs(raw) : null;
          });
          return point;
        }));
      })
      .catch(e => {
        prevChartParamsRef.current = null;
        debug(e);
      });
  };

  const retrieveMaterialPsi = () => {
    const { versionState: version, periodState: period } = searchStateRef.current;

    if (!version.planScope || !version.versionCd) {
      setSnackInfo({ open: true, severity: snackBarMessageColor.inputError, content: t("FP_MSG_SELECT_VERSION") });
      return;
    }
    if (!period.isEntirePlanPeriod && (!period.startDate || !period.endDate)) {
      setSnackInfo({ open: true, severity: snackBarMessageColor.inputError, content: t("FP_MSG_SELECT_PLAN_PERIOD") });
      return;
    }

    const reqId = ++latestGridReqRef.current;
    const params = { ...buildBaseParams(), plantCds: version.plantCd };

    gridRef.current?.api?.setGridOption("loading", true);

    fpMaterialPsiService.getMaterialPsis(params)
      .then(response => {
        if (reqId !== latestGridReqRef.current) return;

        const { crosstabHeader, data } = response.data ?? {};
        const api = gridRef.current?.api;
        if (!api) return;

        const nextColumnDefs = [...fixedColumnDefs, ...buildPivotColumnDefs(crosstabHeader)];
        GridUtils.setColumnDefs({ api, columnDefs: nextColumnDefs, viewName, initState: false });
        applyColumnOrder(api, nextColumnDefs);

        const rows = buildRowData(crosstabHeader, data);
        setRowData(rows);

        if (isEmpty(rows)) {
          clearChart();
          setSnackInfo({ open: true, severity: snackBarMessageColor.dataEmpty, content: t("MSG_NoData") });
          return;
        }

        // old: 조회 직후 첫 행의 공장 기준으로 차트를 그리고, 그리드의 선택 셀도 그 행으로 옮긴다.
        pendingFocusRef.current = { rowIndex: 0, colId: PLANT_CD_COL_ID };
        prevChartParamsRef.current = null;
        loadChart({ plantCd: rows[0].plantCd, plantNm: rows[0].plantNm });
      })
      .catch(e => {
        if (reqId === latestGridReqRef.current) clearChart();
        debug(e);
      })
      .finally(() => {
        if (reqId === latestGridReqRef.current) gridRef.current?.api?.setGridOption("loading", false);
      });
  };

  const onGridReady = (params) => {
    GridUtils.setColumnDefs({ ...params, columnDefs: fixedColumnDefs, viewName, initState: false });
  };

  const onRowDataUpdated = () => consumePendingFocus(gridRef, pendingFocusRef);

  const PLANT_LEVEL_COL_IDS = new Set([PLANT_CD_COL_ID, PLANT_NM_COL_ID]);

  // 클릭한 셀에서 차트를 그릴 드릴다운 대상을 뽑는다(old updateChartFromGrid의
  // `column.startsWith('plant')` 분기를 트리 표시 컬럼 colId 기준으로 옮긴 것). 2단뿐이라
  // PlanResult/ResourceUtilization과 달리 중간 레벨 분기가 없다 — 공장 칸=공장 레벨,
  // 그 외(품목/initStockQty/analysisIndicator/피벗 데이터 칸)는 품목(최하위) 레벨 전체.
  const resolveChartTarget = (params) => {
    const node = params.node;
    if (!node) return null;

    if (node.group) {
      const target = {};
      for (let current = node; current; current = current.parent) {
        if (current.field) target[current.field] = current.key;
      }
      return isEmpty(target) ? null : target;
    }

    const row = node.data;
    if (!row) return null;

    const colId = params.column?.getColId() ?? "";
    if (PLANT_LEVEL_COL_IDS.has(colId)) {
      return { plantCd: row.plantCd, plantNm: row.plantNm };
    }
    return { plantCd: row.plantCd, plantNm: row.plantNm, itemCd: row.itemCd, itemNm: row.itemNm };
  };

  const onCellClicked = (params) => {
    const target = resolveChartTarget(params);
    if (target) loadChart(target);
  };

  const statusBar = useMemo(() => buildReportStatusBar(viewName), [viewName]);

  // 버전이 바뀌면 조회 구간을 다시 계산한다(old setVersionPeriod) — PlanResult/ResourceUtilization과 동일 원리.
  useEffect(() => {
    const version = versionState.selectedVersion;
    if (!version?.startTs) {
      setPeriodState(prev => (prev.startDate || prev.endDate) ? { ...prev, startDate: null, endDate: null } : prev);
      return;
    }

    const startDate = new Date(version.startTs);
    const endDate = addDays(startDate, SEARCH_ZONE_DAYS);
    setPeriodState(prev => ({ ...prev, startDate, endDate }));

    debouncedRetrieveRef.current();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [versionState.selectedVersion]);

  useEffect(() => {
    if (versionState.versionCd) return;

    debouncedRetrieveRef.current.cancel();

    const api = gridRef.current?.api;
    if (api) {
      GridUtils.setColumnDefs({ api, columnDefs: fixedColumnDefs, viewName, initState: false });
      applyColumnOrder(api, fixedColumnDefs);
    }
    setRowData([]);
    clearChart();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [versionState.versionCd]);

  // 선택형 조건(버전/plant/기간/버킷)이 바뀌면 값이 준비되는 즉시 자동 조회한다(§A-9b).
  useEffect(() => {
    const version = searchStateRef.current.versionState;
    const period = searchStateRef.current.periodState;
    if (!version.planScope || !version.versionCd) return;
    if (!period.isEntirePlanPeriod && (!period.startDate || !period.endDate)) return;

    debouncedRetrieveRef.current();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    periodState.startDate, periodState.endDate, periodState.bucketType, periodState.isEntirePlanPeriod,
    versionState.plantCd
  ]);

  // old MaterialPsiChart.jsx는 ChartLegendPlugin을 plugins 배열에 실제로 포함시켜 범례를
  // 보여줬다(`plugins={[ChartLegendPlugin, ChartAxisTitlePlugin]}`, PlanResult와 달리 "설정은
  // 있지만 plugins에 없어 실제로는 없었던" 경우가 아님 — §A-7 "ChartLegendPlugin 판단 시 주의"
  // 참고, old 소스를 직접 확인해 구분했다). → ResourceUtilization과 동일하게 recharts 기본
  // <Legend/>를 CSS로 숨기지 않고 그대로 노출한다. 클릭 시 series 숨김+취소선(old
  // ChartLegendPlugin의 인터랙션)은 Tier2로 유보한다.
  // TODO(MIG-T2): [materialpsi/MaterialPsi] 범례 클릭 시 시리즈 숨김+취소선 미이관.
  //   old: ChartLegendPlugin(DOM 커스텀 범례, 클릭 시 해당 지표 dataset 토글+취소선 스타일)
  //   사유: recharts 기본 Legend/PivotChartComposed에 대응 훅이 없음(가이드 §A-7 Tier2 확정 항목)
  //
  // old ChartAxisTitlePlugin의 좌우 y축 타이틀("(QTY)" / "재고 추이 (QTY)")은 PivotChartComposed의
  // series.groupName 기반 네이티브 YAxis label로 그대로 재현된다(별도 플러그인 불필요, Tier2 아님).
  const leftAxisLabel = useMemo(() => `(${t("QTY")})`, [t]);
  const rightAxisLabel = useMemo(() => `${t("INVENTORY_TREND")} (${t("QTY")})`, [t]);

  const pivotConfig = useMemo(() => ({
    groupKey: "analysisIndicator",
    chartSeries: INDICATOR_ORDER.map(indicator => {
      const conf = INDICATOR_CONFIG[indicator];
      const isLine = conf.type === "line";
      return {
        field: indicator, text: indicator, type: conf.type,
        groupName: isLine ? rightAxisLabel : leftAxisLabel,
        orientation: conf.orientation,
        typeOptions: isLine
          ? { stroke: conf.color, ...(conf.dashed ? { strokeDasharray: "5 3" } : {}), name: t(indicator) }
          : { stackId: conf.stackId, fill: conf.color, name: t(indicator) }
      };
    }),
    include: chartLabels,
    // x축(주+월 등 버킷이 섞이면) 기본 사전순 정렬을 chartLabels 원래 순서로 덮어쓴다
    // (common/chart.js, fp-report-screen-guide.md §A-7 — 크로스탭 차트 화면 공통).
    xAxisSortMap: buildXAxisSortMap(chartLabels)
  }), [chartLabels, leftAxisLabel, rightAxisLabel, t]);

  return (
    <>
      <FilterContainer>
        <VersionSearchGroup
          value={versionState}
          onChange={(partial) => setVersionState(prev => ({ ...prev, ...partial }))}
        />
        <PeriodSearchGroup
          value={periodState}
          onChange={(partial) => setPeriodState(prev => ({ ...prev, ...partial }))}
          // Report 화면 bucketCase 배정표(fp-report-screen-guide.md §A-3) — ResProdPlan/
          // ResrcUtilization 2개 화면만 WITHOUT_HOUR, 그 외 전부(MaterialPsi 포함) WITH_HOUR.
          bucketCase={BUCKET_CASE.WITH_HOUR}
        />
        {/* 조회 버튼 없음(§A-9b, Report 화면 공통 정책) — 자유 텍스트(itemCdNm)는 필드 안의
            Enter/검색아이콘으로 수동 커밋. */}
        <SearchOptsGroupRetrieve
          fieldRefs={{ itemCdNm: itemCdNmRef }}
          fieldKeys={SEARCH_FIELD_KEYS}
          labels={SEARCH_FIELD_LABELS}
          retrieve={retrieveMaterialPsi}
        />
        <Box sx={{ display: "flex" }}>
          <DownloadButton
            onClick={() => gridRef.current?.api?.exportDataAsExcel({ fileName: `${t(title)}.xlsx`, sheetName: t(title) })}
          />
        </Box>
      </FilterContainer>
      {/* old VerticalGridChartBox의 sizes={[50, 50]}(direction="vertical") 대응 — SplitterContainer의
          기본 initialSizes([3, 2], 60/40)를 그대로 두면 그리드:차트가 1:1이 아니게 된다(§A-1c). */}
      <SplitterContainer initialSizes={[1, 1]}>
        <Box id="fp-material-psi-grid" className="ag-theme-balham grid-area" sx={{ height: "calc(100% - 8px)", pb: 1 }}>
          <AgGridReact
            ref={gridRef}
            rowData={rowData}
            {...defaultGridMemo}
            statusBar={statusBar}
            onGridReady={onGridReady}
            onRowDataUpdated={onRowDataUpdated}
            onCellClicked={onCellClicked}
          />
        </Box>
        <Box className="chart-area" sx={{ height: "calc(100% - 8px)", mt: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>
          <Box sx={{ flex: 1, minHeight: 0, overflow: "hidden" }}>
            <PivotChartComposed
              pivotConfig={pivotConfig}
              data={chartData}
              options={{ tooltipContentStyle: { fontSize: "12px" }, minHeight: "0px", decimalPoint: true }}
              showConfigurationPopup={false}
              nullConvert
              customTooltip={AbsValueTooltip}
            />
          </Box>
        </Box>
      </SplitterContainer>
      <Snackbar
        open={snackInfo.open}
        onClose={() => setSnackInfo({ ...snackInfo, open: false })}
        severity={snackInfo.severity}
        content={snackInfo.content}
      />
    </>
  );
};

export default withTranslation()(MaterialPsi);
